import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useDispatch, useSelector } from 'react-redux';
import { setCommandMessage } from '../../../redux/operationSlice';
import { createBundleFromTwoSlices, expandBundle, findBundleContaining, rebuildBundleWithStubs } from '../../../utils/wireBundling';
import { findPanelExitPoints, updatePanelWiringPositions } from '../utils/panelExitManager';
import { BundleCommand } from '../../../modules/undoRedo';

/**
 * Wire bundling — iki aşamalı, her aşamada iki tıklama (toplam 4):
 *   1. Ctrl+B → bundle modu toggle.
 *   2. Aşama 1 — BİRLEŞTİRME hattı: 2 tıklama ile kabloları kesen bir hat çiz.
 *   3. Aşama 2 — AÇMA hattı:        2 tıklama ile diğer kesim hattını çiz.
 *      → Her iki hattı da kesen paralel wire'lar bundle olur.
 *   - Esc her aşamada iptal eder.
 *   - Ctrl+Shift+B: seçili wire bir bundle'a aitse onu açar.
 */
export function useWireBundling(scene, camera, renderer) {
  const dispatch = useDispatch();
  const selectedObjectIds = useSelector(s => s.selection.selectedObjectIds);

  const bundleModeRef = useRef(false);
  // 4-tıklama akışı: clicks 0,1 = BİRLEŞTİRME hattı; clicks 2,3 = AÇMA hattı.
  const clicksRef = useRef([]);           // dünya koordinatlarında tıklama noktaları
  const previewMeshRef = useRef(null);    // aktif aşamanın 1. tıklamasından mouse'a önizleme
  const mergeSliceMeshRef = useRef(null); // aşama 1 tamamlandığında sabit yeşil hat
  const tooltipRef = useRef(null);        // mouse'u takip eden talimat tooltip'i
  // selection değişikliği effect'i remount etmesin diye ref üzerinden okunuyor;
  // aksi halde 1. tıklama selection'ı değiştirir → cleanup çalışır → state sıfırlanır →
  // 2. tıklama başka bir 1. tıklama gibi davranır.
  const selectedIdsRef = useRef(selectedObjectIds);
  selectedIdsRef.current = selectedObjectIds;
  // M-drag: collector'ı sürükleme state'i
  const mHeldRef = useRef(false);
  const mDragRef = useRef(null);          // { bundle, side: 'entry'|'exit', startWorld, initialLen }
  const mDragPendingRef = useRef(null);   // { backLen, forwLen } — rAF ile coalesce
  const mDragRafRef = useRef(0);
  // Slice-slide drag: collector'a modifier'sız tıklayıp wire güzergâhı boyunca
  // kaydır → ilgili slice o pozisyona ötelenir, bundle anında yeniden inşa.
  const slideDragRef = useRef(null);      // { bundle, sliceKey, sliceLen, raf }
  // Panel-side bundle drag: bundle entry collector'ına click+drag → panel exit
  // index'lerinde grup halinde kaydırır (3-4-5 → 4-5-6 vb.). bundle.userData.
  // panelExitRange varsa aktif olur.
  const bundlePanelDragRef = useRef(null); // { bundle, panel, panelLocalY, startWorld, cumulativeShift }
  // Pending: collector tıklandığında modu (slice-slide vs bundle panel-drag) ilk
  // hareket yönüne göre seç. Her iki modun setup verisi burada hazır beklenir.
  const pendingCollectorDragRef = useRef(null);

  // Phase 3 helper: bundle'ı panel exit indices'inde 1 slot kaydır (yön = ±1).
  // outs[] dizisinde [min..max+dir] aralığını siklik döndürür → bundle wire'ları
  // bir indexe daha geçer, sınırdaki non-bundle wire da boşalan slota düşer.
  // Sonra updatePanelWiringPositions panel görsellerini outs sırasına hizalar.
  // Bundle group ve içindeki extra line'lar offset kadar translate edilir →
  // collector cluster + slice + rep line görseli wire'larla birlikte hareket eder.
  // Sınır aşımında (newMin<0 veya newMax>=N) false döner.
  const shiftBundleByOneSlot = (bundle, panel, direction) => {
    const range = bundle?.userData?.panelExitRange;
    if (!range || !panel || !panel.userData) return false;
    const outs = panel.userData.outs;
    if (!Array.isArray(outs)) return false;
    const exitPoints = findPanelExitPoints(panel);
    const N = exitPoints.length;
    const newMin = range.min + direction;
    const newMax = range.max + direction;
    if (newMin < 0 || newMax >= N || newMin >= outs.length || newMax >= outs.length) return false;

    // Eski/yeni exit point pozisyonlarına göre WORLD offset hesapla — bundle'ın
    // "min" exit point'inden newMin exit point'ine olan vektör. exitPoints panel
    // local coord'larında olduğu için world'e dönüştürmek gerek (rotated panel için).
    const oldP = exitPoints[range.min];
    const newP = exitPoints[newMin];
    if (!oldP || !newP) return false;
    const _wOld = new THREE.Vector3();
    const _wNew = new THREE.Vector3();
    oldP.getWorldPosition(_wOld);
    newP.getWorldPosition(_wNew);
    const offX = _wNew.x - _wOld.x;
    const offY = _wNew.y - _wOld.y;

    // outs aralığını siklik döndür: [min..max+dir]
    const lo = Math.min(range.min, newMin);
    const hi = Math.max(range.max, newMax);
    const slice = outs.slice(lo, hi + 1);
    let rotated;
    if (direction > 0) {
      rotated = [slice[slice.length - 1], ...slice.slice(0, -1)];
    } else {
      rotated = [...slice.slice(1), slice[0]];
    }
    for (let i = 0; i < slice.length; i++) outs[lo + i] = rotated[i];

    // Bundle range güncelle
    range.min = newMin;
    range.max = newMax;
    if (Array.isArray(range.indices)) {
      range.indices = range.indices.map(idx => idx + direction);
    }

    // Bundle group görsellerini translate et (collector'lar, rep line)
    bundle.position.x += offX;
    bundle.position.y += offY;

    // Bundle slice/path metadata'sını da kaydır — sonraki rebuild/snap için doğru kalsın
    const translatePoint = (p) => { if (p) { p.x += offX; p.y += offY; } };
    if (bundle.userData.slices) {
      translatePoint(bundle.userData.slices.merge?.a);
      translatePoint(bundle.userData.slices.merge?.b);
      translatePoint(bundle.userData.slices.open?.a);
      translatePoint(bundle.userData.slices.open?.b);
    }
    if (bundle.userData.points) {
      translatePoint(bundle.userData.points.a);
      translatePoint(bundle.userData.points.b);
    }
    if (Array.isArray(bundle.userData.guidePath)) {
      bundle.userData.guidePath.forEach(translatePoint);
    }

    // Extra line'lar (truncate sonrası wire'ın forw-side parçası) bundleGroup
    // child'ı değil — sahnede ayrı duran Line'lar. Onları da offset kadar shift et
    // ki wire-extraLine bağlantısı kopmasın.
    const extraUuids = new Set(bundle.userData.extraLineUuids || []);
    if (extraUuids.size > 0) {
      scene.traverse(obj => {
        if (!extraUuids.has(obj.uuid)) return;
        obj.position.x += offX;
        obj.position.y += offY;
      });
    }

    // Wire first edge'leri yeni exit point'lere hizala
    try { updatePanelWiringPositions(scene, panel); } catch (e) { /* ignore */ }
    return true;
  };

  useEffect(() => {
    if (!scene || !camera || !renderer) return;
    const dom = renderer.domElement;

    const getWorld = (e) => {
      const r = dom.getBoundingClientRect();
      const nx = ((e.clientX - r.left) / r.width) * 2 - 1;
      const ny = -((e.clientY - r.top) / r.height) * 2 + 1;
      const v = new THREE.Vector3(nx, ny, 0).unproject(camera);
      return { x: v.x, y: v.y };
    };

    // Mouse'u takip eden talimat tooltip'i — Ctrl+B'den itibaren görünür.
    const ensureTooltip = () => {
      if (tooltipRef.current) return tooltipRef.current;
      const el = document.createElement('div');
      el.style.cssText = [
        'position:fixed', 'pointer-events:none', 'z-index:100000',
        'background:rgba(20,30,40,0.92)', 'color:#aff', 'border:1px solid #0aa',
        'padding:5px 9px', 'border-radius:4px',
        'font:12px/1.3 "Segoe UI",sans-serif', 'white-space:nowrap',
        'box-shadow:0 2px 8px rgba(0,0,0,0.4)', 'left:-9999px', 'top:-9999px',
      ].join(';');
      document.body.appendChild(el);
      tooltipRef.current = el;
      return el;
    };
    const setTooltipText = (text) => {
      const el = ensureTooltip();
      el.textContent = text;
    };
    const moveTooltip = (clientX, clientY) => {
      if (!tooltipRef.current) return;
      tooltipRef.current.style.left = `${clientX + 14}px`;
      tooltipRef.current.style.top  = `${clientY + 14}px`;
    };
    const removeTooltip = () => {
      if (tooltipRef.current) {
        tooltipRef.current.remove();
        tooltipRef.current = null;
      }
    };

    // Aktif aşamanın ilk tıklamasından mouse'a uzanan yeşil dashed önizleme.
    const updatePreviewLine = (start, end) => {
      if (previewMeshRef.current) {
        scene.remove(previewMeshRef.current);
        previewMeshRef.current.geometry.dispose();
        if (previewMeshRef.current.material.dispose) previewMeshRef.current.material.dispose();
        previewMeshRef.current = null;
      }
      if (!start || !end) return;
      const pts = [
        new THREE.Vector3(start.x, start.y, 0.1),
        new THREE.Vector3(end.x, end.y, 0.1),
      ];
      const geom = new THREE.BufferGeometry().setFromPoints(pts);
      const mat = new THREE.LineDashedMaterial({ color: 0x00ff00, dashSize: 2, gapSize: 1 });
      const mesh = new THREE.Line(geom, mat);
      mesh.computeLineDistances();
      mesh.userData = { isTransient: true, isBundleSelection: true };
      scene.add(mesh);
      previewMeshRef.current = mesh;
    };

    // Aşama 1 tamamlanınca sabit yeşil (kesintisiz) hat çiz — kullanıcı 2.
    // aşamayı çizerken referans olsun.
    const setMergeSliceMesh = (a, b) => {
      if (mergeSliceMeshRef.current) {
        scene.remove(mergeSliceMeshRef.current);
        mergeSliceMeshRef.current.geometry.dispose();
        if (mergeSliceMeshRef.current.material.dispose) mergeSliceMeshRef.current.material.dispose();
        mergeSliceMeshRef.current = null;
      }
      if (!a || !b) return;
      const pts = [
        new THREE.Vector3(a.x, a.y, 0.1),
        new THREE.Vector3(b.x, b.y, 0.1),
      ];
      const geom = new THREE.BufferGeometry().setFromPoints(pts);
      const mat = new THREE.LineBasicMaterial({ color: 0x00cc00 });
      const mesh = new THREE.Line(geom, mat);
      mesh.userData = { isTransient: true, isBundleSelection: true };
      scene.add(mesh);
      mergeSliceMeshRef.current = mesh;
    };

    const cancelBundleMode = () => {
      bundleModeRef.current = false;
      clicksRef.current = [];
      updatePreviewLine(null, null);
      setMergeSliceMesh(null, null);
      removeTooltip();
      dom.style.cursor = '';
      dispatch(setCommandMessage(''));
    };

    const finishBundle = () => {
      const c = clicksRef.current;
      clicksRef.current = [];
      updatePreviewLine(null, null);
      setMergeSliceMesh(null, null);
      removeTooltip();
      if (c.length !== 4) return;
      const mergeSlice = { a: c[0], b: c[1] };
      const openSlice  = { a: c[2], b: c[3] };
      // Hatlar arasında yeterli mesafe var mı?
      const mergeMid = { x: (c[0].x + c[1].x) / 2, y: (c[0].y + c[1].y) / 2 };
      const openMid  = { x: (c[2].x + c[3].x) / 2, y: (c[2].y + c[3].y) / 2 };
      if (Math.hypot(openMid.x - mergeMid.x, openMid.y - mergeMid.y) < 2) {
        dispatch(setCommandMessage('Birleştirme ve açma hatları arasında yeterli mesafe yok.'));
        setTimeout(() => dispatch(setCommandMessage('')), 2500);
        return;
      }

      const bundle = createBundleFromTwoSlices(scene, mergeSlice, openSlice);
      if (bundle) {
        // ── Undo/Redo kaydı ──────────────────────────────────────────────
        const _mgr = window.undoRedoManager;
        if (_mgr) {
          const _origGeoms = bundle.userData.originalGeometries || [];
          // Before states: bundle'dan önce tam wire geometrileri (bundleGroup içinde saklanıyor)
          const _wireBeforeStates = _origGeoms.map(og => ({
            wireId: og.wireId,
            positions: Array.from(og.positions)
          }));
          // After states: bundle sonrası kısaltılmış wire geometrileri (şu an sahnede)
          const _wireAfterStates = _wireBeforeStates.map(ws => {
            let _pos = null;
            scene.traverse(obj => {
              if (obj.isLine && obj.userData?.id === ws.wireId) {
                _pos = Array.from(obj.geometry.attributes.position.array);
              }
            });
            return { wireId: ws.wireId, positions: _pos || ws.positions };
          });
          // Extra line'lar (stub çizgileri)
          const _extraUuids = new Set(bundle.userData.extraLineUuids || []);
          const _extraLines = [];
          scene.traverse(obj => { if (_extraUuids.has(obj.uuid)) _extraLines.push(obj); });

          const _tx = _mgr.beginTransaction('Bundle Wires');
          _mgr.addCommand(new BundleCommand(
            'CREATE', bundle, _wireBeforeStates, _wireAfterStates,
            _extraLines, bundle.userData.hiddenMarkerUuids || []
          ));
          _mgr.commitTransaction(_tx);
        }
        // ─────────────────────────────────────────────────────────────────
        const count = (bundle.userData.wireIds || []).length;
        dispatch(setCommandMessage(`${count} wire bundle edildi.`));
        setTimeout(() => dispatch(setCommandMessage('')), 2500);
      } else {
        dispatch(setCommandMessage('İki hattı da kesen paralel wire bulunamadı.'));
        setTimeout(() => dispatch(setCommandMessage('')), 2500);
      }
    };

    // Bir bundle'ın entry/exit collector'ı mouse world noktasına yakın mı?
    // Hem bounding box hem de çizgi segmentlerine dik mesafe ile kontrol et
    // — bbox ince kaldığında segment mesafesi devreye girer.
    const PICK_TOL = 12; // birim cinsinden tolerans
    const findBundleCollectorAtPoint = (worldPt) => {
      let found = null;
      let bestDist = Infinity;
      const pt = { x: worldPt.x, y: worldPt.y };
      scene.traverse(obj => {
        if (obj.userData?.type !== 'wireBundle') return;
        for (const child of obj.children) {
          const role = child.userData?.role;
          if (role !== 'collector-entry' && role !== 'collector-exit') continue;
          // Grup içindeki her Line'ın iki uç noktasına dik mesafe
          let minSegD = Infinity;
          child.traverse(ln => {
            if (!ln.isLine || !ln.geometry?.attributes?.position) return;
            const pos = ln.geometry.attributes.position.array;
            if (pos.length < 6) return;
            const ax = pos[0], ay = pos[1];
            const bx = pos[3], by = pos[4];
            const vx = bx - ax, vy = by - ay;
            const wx = pt.x - ax, wy = pt.y - ay;
            const len2 = vx * vx + vy * vy || 1;
            let t = (wx * vx + wy * vy) / len2;
            t = Math.max(0, Math.min(1, t));
            const cx = ax + vx * t, cy = ay + vy * t;
            const d = Math.hypot(pt.x - cx, pt.y - cy);
            if (d < minSegD) minSegD = d;
          });
          if (minSegD > PICK_TOL) continue;
          if (minSegD < bestDist) {
            bestDist = minSegD;
            found = { bundle: obj, side: role === 'collector-entry' ? 'entry' : 'exit', collector: child };
          }
        }
      });
      return found;
    };

    // Bir noktayı bundle'ın guidePath'ine (sabit referans güzergâh) projekte et.
    // Bundle yaratımında tüm wire'ların ortalama tam-yol güzergâhı üretilir;
    // rebuild'lerle değişmediği için drag boyunca tutarlı projeksiyon verir.
    const projectOnGuidePath = (bundle, worldPt) => {
      const guide = bundle.userData?.guidePath;
      if (!guide || guide.length < 2) return null;
      let best = null;
      for (let i = 0; i < guide.length - 1; i++) {
        const a = guide[i], b = guide[i + 1];
        const dx = b.x - a.x, dy = b.y - a.y;
        const lenSq = dx * dx + dy * dy;
        if (lenSq < 1e-9) continue;
        let t = ((worldPt.x - a.x) * dx + (worldPt.y - a.y) * dy) / lenSq;
        t = Math.max(0, Math.min(1, t));
        const px = a.x + dx * t, py = a.y + dy * t;
        const d = Math.hypot(worldPt.x - px, worldPt.y - py);
        if (!best || d < best.dist) {
          const tlen = Math.sqrt(lenSq);
          best = { point: { x: px, y: py }, tangent: { x: dx / tlen, y: dy / tlen }, dist: d, segIdx: i, t };
        }
      }
      return best;
    };

    // Slice'ı köşe çevresinde "köprüle": her köşenin gerçek transition zone'u
    // (wire vertex'lerinin guide'a projeksiyonu min/max arc) + 5 birim safety
    // ile bridge zone tanımlanır. Slice bu zone'a girerse karşı tarafa sıçrar.
    // Böylece wire'lar her zaman aynı segmentte kalır → rep line/collector temiz.
    // Bridge zone tabanlı snap — guidePath arc-length üzerinden.
    // Her köşenin "transition zone"u (wire vertex'lerinin guide arc range'i) +
    // safety içine slice arc düşerse o köşe için snap fire. Euclidean point
    // distance kullanmıyoruz çünkü perpendicular wire offset'i nedeniyle slice
    // midpoint inner corner'a hiçbir zaman çok yaklaşamaz.
    // Snap target: inner corner + 10 birim offset (görsel kompakt).
    // Snap: proj köşeye 5 arc-birim yaklaşana kadar serbest, 5'te tetiklenir →
    // slice korner'ın diğer tarafına X=10 birim sıçrar (köşe geçilir).
    // Hem merge hem open için aynı mantık. Önceki "open için X=-X" implementasyonu
    // 90°'lik köşelerde slice'ı wire dışına atıyordu → bundle yok oluyordu.
    const snapProjToCorner = (bundle, proj, sliceKey) => {
      const guide = bundle.userData?.guidePath;
      const zones = bundle.userData?.guideBridgeZones;
      const inners = bundle.userData?.innerCorners;
      const wireCount = bundle.userData?.wireCount || 0;
      if (!guide || guide.length < 2 || !zones?.length || !inners?.length || wireCount < 2) return proj;

      const TRIGGER_DIST = 5; // arc-length from corner center
      const SNAP_OFFSET = 10;

      // proj arc on guide
      let projArc = 0;
      for (let i = 0; i < proj.segIdx; i++) {
        projArc += Math.hypot(guide[i + 1].x - guide[i].x, guide[i + 1].y - guide[i].y);
      }
      const segLen0 = Math.hypot(guide[proj.segIdx + 1].x - guide[proj.segIdx].x,
                                  guide[proj.segIdx + 1].y - guide[proj.segIdx].y);
      projArc += proj.t * segLen0;

      for (let zi = 0; zi < zones.length && zi < inners.length; zi++) {
        const zone = zones[zi];
        const cornerArc = (zone.min + zone.max) / 2;
        const distToCorner = Math.abs(projArc - cornerArc);
        if (distToCorner > TRIGGER_DIST) continue;
        const ic = inners[zi];
        const dx = proj.point.x - ic.point.x;
        const dy = proj.point.y - ic.point.y;
        const dotBefore = dx * ic.beforeDir.x + dy * ic.beforeDir.y;
        const dotPast   = dx * ic.pastDir.x   + dy * ic.pastDir.y;
        const isOnBeforeSide = dotBefore > dotPast;
        // Merge → OPPOSITE side wire-dir (push past corner).
        // Open  → SAME side wire-dir (pull back, slice korner'a girmez).
        // Her iki durumda da snapDir bir wire teğeti → 90° köşelerde wire üzerinde kalır.
        let snapDir;
        if (sliceKey === 'open') {
          snapDir = isOnBeforeSide ? ic.beforeDir : ic.pastDir;
        } else {
          snapDir = isOnBeforeSide ? ic.pastDir : ic.beforeDir;
        }
        const sx = ic.point.x + SNAP_OFFSET * snapDir.x;
        const sy = ic.point.y + SNAP_OFFSET * snapDir.y;
        return {
          point: { x: sx, y: sy },
          tangent: { x: snapDir.x, y: snapDir.y },
          dist: 0, segIdx: 0, t: 0,
        };
      }
      return proj;
    };

    // Collector normalini (hat yönünü) hesapla — sürüklemenin yön projeksiyonu
    // bu vektör üzerinde yapılır. Entry/exit için bundle path'inin ilk/son segment
    // yönünü döndürür.
    const getCollectorAxisDir = (bundle, side) => {
      // Representative polyline: bundle'ın direct child'ı olan Line objesi
      // (collectors THREE.Group — Line değil; representative THREE.Line)
      let repLine = null;
      for (const c of bundle.children) {
        if (c.isLine && c.geometry?.attributes?.position) {
          repLine = c;
          break;
        }
      }
      if (!repLine) return { x: 1, y: 0 };
      const pos = repLine.geometry.attributes.position.array;
      const n = pos.length / 3;
      if (n < 2) return { x: 1, y: 0 };
      let ax, ay, bx, by;
      if (side === 'entry') {
        // İlk iki nokta: giriş yönü (bundle içine doğru)
        ax = pos[0]; ay = pos[1];
        bx = pos[3]; by = pos[4];
      } else {
        // Son iki nokta: çıkış yönü (bundle içinden dışarı)
        const k = (n - 1) * 3;
        ax = pos[k - 3]; ay = pos[k - 2];
        bx = pos[k];     by = pos[k + 1];
      }
      const dx = bx - ax, dy = by - ay;
      const L = Math.hypot(dx, dy) || 1;
      return { x: dx / L, y: dy / L };
    };

    // ── Keyboard listeners ──
    const onKeyDown = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;

      // Esc → bundle modunu/seçim akışını iptal
      if (e.key === 'Escape' && bundleModeRef.current) {
        e.preventDefault();
        cancelBundleMode();
        return;
      }

      // M tuşu — collector drag modu
      if ((e.key === 'm' || e.key === 'M') && !e.ctrlKey && !e.metaKey) {
        mHeldRef.current = true;
        dom.style.cursor = 'ew-resize';
        dispatch(setCommandMessage('Collector\'a tıklayıp sürükleyerek bundle boyutunu ayarlayın'));
        return;
      }

      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key !== 'b' && e.key !== 'B') return;
      e.preventDefault();

      if (e.shiftKey) {
        // Expand — seçili wire bir bundle'a aitse onu aç
        let expanded = 0;
        for (const id of (selectedIdsRef.current || [])) {
          const b = findBundleContaining(scene, id);
          if (!b) continue;

          const _mgr = window.undoRedoManager;
          if (_mgr) {
            // Before states: şu an sahnedeki kısaltılmış geometriler (expand öncesi)
            const _origGeoms = b.userData.originalGeometries || [];
            const _wireBeforeStates = _origGeoms.map(og => {
              let _pos = null;
              scene.traverse(obj => {
                if (obj.isLine && (obj.uuid === og.uuid || obj.userData?.id === og.wireId)) {
                  _pos = Array.from(obj.geometry.attributes.position.array);
                }
              });
              return { wireId: og.wireId, positions: _pos || Array.from(og.positions) };
            });
            // After states: orijinal tam geometriler (expand sonrası geri yüklenecek)
            const _wireAfterStates = _origGeoms.map(og => ({
              wireId: og.wireId,
              positions: Array.from(og.positions)
            }));
            const _markerUuids = [...(b.userData.hiddenMarkerUuids || [])];
            const _extraUuids = new Set(b.userData.extraLineUuids || []);
            const _extraLines = [];
            scene.traverse(obj => { if (_extraUuids.has(obj.uuid)) _extraLines.push(obj); });

            // Dispose çağırmadan manuel expand: orijinal geometrileri geri yükle
            for (const el of _extraLines) { if (el.parent) el.parent.remove(el); }
            scene.traverse(obj => { if (_markerUuids.includes(obj.uuid)) obj.visible = true; });
            for (const og of _origGeoms) {
              let _wire = null;
              scene.traverse(obj => {
                if (!_wire && (obj.uuid === og.uuid || obj.userData?.id === og.wireId)) _wire = obj;
              });
              if (!_wire) continue;
              const _arr = new Float32Array(og.positions);
              _wire.geometry.setAttribute('position', new THREE.BufferAttribute(_arr, 3));
              _wire.geometry.attributes.position.needsUpdate = true;
              _wire.geometry.computeBoundingSphere();
              if (typeof _wire.computeLineDistances === 'function') _wire.computeLineDistances();
              _wire.visible = og.origVisible !== false;
            }
            scene.remove(b); // dispose yok — undo için canlı tutulur

            const _tx = _mgr.beginTransaction('Expand Bundle');
            _mgr.addCommand(new BundleCommand(
              'EXPAND', b, _wireBeforeStates, _wireAfterStates,
              _extraLines, _markerUuids
            ));
            _mgr.commitTransaction(_tx);
            expanded++;
          } else {
            if (expandBundle(scene, b)) expanded++;
          }
        }
        if (expanded > 0) {
          dispatch(setCommandMessage(`${expanded} bundle açıldı.`));
          setTimeout(() => dispatch(setCommandMessage('')), 2500);
        } else {
          dispatch(setCommandMessage('Seçili wire bir bundle içinde değil.'));
          setTimeout(() => dispatch(setCommandMessage('')), 2500);
        }
        return;
      }

      // Bundle modu toggle — açık değilse aç (Ctrl+B basılı tutmaya gerek yok)
      if (!bundleModeRef.current) {
        bundleModeRef.current = true;
        clicksRef.current = [];
        dom.style.cursor = 'crosshair';
        setTooltipText('BİRLEŞTİRME hattı — 1. noktayı tıkla');
        dispatch(setCommandMessage('BİRLEŞTİRME hattının 1. noktasını tıklayın (Esc iptal)'));
      } else {
        // Mod zaten açıksa Ctrl+B tekrar basılırsa iptal
        cancelBundleMode();
      }
    };

    const onKeyUp = (e) => {
      if (e.key === 'm' || e.key === 'M') {
        mHeldRef.current = false;
        if (!mDragRef.current) {
          dom.style.cursor = '';
          if (!bundleModeRef.current) dispatch(setCommandMessage(''));
        }
        return;
      }
      // Bundle modu toggle: Ctrl/B release artık modu kapatmıyor.
    };

    // ── Mouse listeners ──
    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      const worldPt = getWorld(e);

      // M-drag önceliği: collector'a tıklandı mı?
      if (mHeldRef.current) {
        const hit = findBundleCollectorAtPoint(worldPt);
        if (hit) {
          // Panel hareket modu ile çakışmayı engelle: capture phase'te aynı anda
          // attached usePanelMove handler'ı da çalışır. stopImmediatePropagation
          // usePanelMove'un mousedown'u tüketmesini engellemez (zaten kendi
          // handler'ında gecikmeli çalışır); bunun yerine bir defaya mahsus
          // panelMoveModeRef'i false'a çekip drag bitince geri yüklüyoruz.
          e.preventDefault();
          e.stopPropagation();
          if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
          const initialLen = hit.side === 'entry'
            ? (hit.bundle.userData.backStubLen || 20)
            : (hit.bundle.userData.forwStubLen || 20);
          const axisDir = getCollectorAxisDir(hit.bundle, hit.side);
          // axisDir = representative polyline yönü
          //   entry: ilk segment yönü → bundle İÇİNE doğru (+)
          //   exit : son segment yönü → bundle DIŞINA doğru (+)
          // stubLen arttıkça stubEnd (= collector) bundle MERKEZİNE doğru kayar.
          // Collector mouse ile aynı yönde kaysın diye "followDir" şöyle olmalı:
          //   entry: mouse içeriye (+axisDir) → stubLen arts → collector içeri  ✓
          //   exit : mouse dışarı (+axisDir) → stubLen azal → collector dışarı  ✓
          //   ⇒ followDir = entry ? +axisDir : -axisDir
          const followDir = hit.side === 'entry'
            ? { x: axisDir.x, y: axisDir.y }
            : { x: -axisDir.x, y: -axisDir.y };
          mDragRef.current = {
            bundle: hit.bundle,
            side: hit.side,
            startWorld: worldPt,
            initialLen,
            followDir,
          };
          return;
        }
      }

      // Collector tıklama: slice-slide (wire boyunca) vs bundle panel-drag (panel'e dik)
      // ilk hareket yönüne göre seçilir. Setup verisi pending ref'te bekler;
      // mousemove'da ilk delta'nın projeksiyonu ile mod commit edilir.
      if (!mHeldRef.current && !bundleModeRef.current) {
        const hit = findBundleCollectorAtPoint(worldPt);
        if (hit && hit.bundle.userData?.slices) {
          e.preventDefault();
          e.stopPropagation();
          if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

          const slices = hit.bundle.userData.slices;
          const mid = (s) => ({ x: (s.a.x + s.b.x) / 2, y: (s.a.y + s.b.y) / 2 });
          const mM = mid(slices.merge), mO = mid(slices.open);
          const dM = Math.hypot(worldPt.x - mM.x, worldPt.y - mM.y);
          const dO = Math.hypot(worldPt.x - mO.x, worldPt.y - mO.y);
          const sliceKey = dM <= dO ? 'merge' : 'open';
          const slice = slices[sliceKey];
          const sliceMid = { x: (slice.a.x + slice.b.x) / 2, y: (slice.a.y + slice.b.y) / 2 };
          const sliceLen = Math.hypot(slice.b.x - slice.a.x, slice.b.y - slice.a.y);

          // wireDir = slice'ın perpendicular yönü (= wire boyunca yön)
          const sliceDx = slice.b.x - slice.a.x, sliceDy = slice.b.y - slice.a.y;
          const sliceLn = Math.hypot(sliceDx, sliceDy) || 1;
          const wireDir = { x: -sliceDy / sliceLn, y: sliceDx / sliceLn };

          // Bundle panel-drag varsa: panelExitRange + panel + panelLocalY hazırla
          let panelDragData = null;
          const _range = hit.bundle.userData?.panelExitRange;
          if (_range && _range.panelID) {
            let panel = null;
            scene.traverse(obj => { if (panel) return; if (obj.userData?.id === _range.panelID) panel = obj; });
            if (panel) {
              const theta = panel.rotation?.z || 0;
              const panelLocalY = { x: -Math.sin(theta), y: Math.cos(theta) };
              panelDragData = { panel, panelLocalY };
            }
          }

          pendingCollectorDragRef.current = {
            hit,
            bundle: hit.bundle,
            sliceKey,
            sliceLen,
            sliceMid,
            wireDir,
            panelDragData, // null değilse panel-drag opsiyonu var
            startWorld: { x: worldPt.x, y: worldPt.y },
          };
          dom.style.cursor = 'grabbing';
          return;
        }
      }

      // Eski yol: doğrudan slice-slide (artık pendingCollectorDragRef üzerinden gidiyor,
      // bu blok unreachable; geriye uyumluluk için tutuldu).
      if (false && !mHeldRef.current && !bundleModeRef.current) {
        const hit = findBundleCollectorAtPoint(worldPt);
        if (hit && hit.bundle.userData?.slices) {
          const slices = hit.bundle.userData.slices;
          const mid = (s) => ({ x: (s.a.x + s.b.x) / 2, y: (s.a.y + s.b.y) / 2 });
          const mM = mid(slices.merge), mO = mid(slices.open);
          const dM = Math.hypot(worldPt.x - mM.x, worldPt.y - mM.y);
          const dO = Math.hypot(worldPt.x - mO.x, worldPt.y - mO.y);
          const sliceKey = dM <= dO ? 'merge' : 'open';
          const slice = slices[sliceKey];
          const sliceMid = { x: (slice.a.x + slice.b.x) / 2, y: (slice.a.y + slice.b.y) / 2 };
          const sliceLen = Math.hypot(slice.b.x - slice.a.x, slice.b.y - slice.a.y);
          slideDragRef.current = {
            bundle: hit.bundle,
            sliceKey,
            sliceLen,
            startMouse: { x: worldPt.x, y: worldPt.y },
            origSliceMid: sliceMid,
            raf: 0,
            pending: null,
          };
          dom.style.cursor = 'grabbing';
          return;
        }
      }

      if (!bundleModeRef.current) return;
      // Bundle modunda capture-phase'te tıklamayı tamamen tüket — useSelection
      // ve diğer click consumer'ların aynı event'i işlemesini engelle.
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

      clicksRef.current.push(worldPt);
      const n = clicksRef.current.length;

      if (n === 1) {
        // BİRLEŞTİRME hattı - 1. nokta kaydedildi, 2. noktayı bekle
        updatePreviewLine(worldPt, worldPt);
        setTooltipText('BİRLEŞTİRME hattı — 2. noktayı tıkla');
        dispatch(setCommandMessage('BİRLEŞTİRME hattının 2. noktasını tıklayın'));
      } else if (n === 2) {
        // BİRLEŞTİRME hattı tamamlandı, sabit yeşil hat olarak göster
        updatePreviewLine(null, null);
        setMergeSliceMesh(clicksRef.current[0], clicksRef.current[1]);
        setTooltipText('AÇMA hattı — 1. noktayı tıkla');
        dispatch(setCommandMessage('AÇMA hattının 1. noktasını tıklayın'));
      } else if (n === 3) {
        // AÇMA hattı - 1. nokta kaydedildi, 2. noktayı bekle
        updatePreviewLine(worldPt, worldPt);
        setTooltipText('AÇMA hattı — 2. noktayı tıkla');
        dispatch(setCommandMessage('AÇMA hattının 2. noktasını tıklayın'));
      } else if (n >= 4) {
        // AÇMA hattı tamamlandı → bundle'ı oluştur ve modu kapat
        finishBundle();
        bundleModeRef.current = false;
        dom.style.cursor = '';
      }
    };

    const onMouseMove = (e) => {
      // Phase 3 — Bundle panel-side grup drag: mouse'un panel local Y'sine olan
      // delta'sı / step (5 birim) ile shift miktarı belirlenir. Cumulative shift
      // takip edilir; yeni hedef shift'e ulaşana kadar shiftBundleByOneSlot
      // çağrılır (sınır aşımında kırılır).
      if (bundlePanelDragRef.current) {
        const w = getWorld(e);
        const cur = bundlePanelDragRef.current;
        const dx = w.x - cur.startWorld.x;
        const dy = w.y - cur.startWorld.y;
        // Panel local Y eksenine projekte
        const projLocalY = dx * cur.panelLocalY.x + dy * cur.panelLocalY.y;
        const targetShift = Math.round(projLocalY / cur.step);
        let diff = targetShift - cur.cumulativeShift;
        if (diff !== 0) {
          const dir = diff > 0 ? 1 : -1;
          let steps = Math.abs(diff);
          while (steps > 0) {
            const ok = shiftBundleByOneSlot(cur.bundle, cur.panel, dir);
            if (!ok) break;
            cur.cumulativeShift += dir;
            steps--;
          }
        }
        return;
      }

      // Pending collector click: ilk hareket yönüne göre slice-slide ya da bundle
      // panel-drag mod'unu commit et.
      if (pendingCollectorDragRef.current) {
        const pd = pendingCollectorDragRef.current;
        const w = getWorld(e);
        const dx = w.x - pd.startWorld.x;
        const dy = w.y - pd.startWorld.y;
        const distSq = dx * dx + dy * dy;
        const THRESH = 3;
        if (distSq < THRESH * THRESH) {
          // Henüz yeterli hareket yok — bekle
          return;
        }
        // Yön projeksiyonları
        const projWire = Math.abs(dx * pd.wireDir.x + dy * pd.wireDir.y);
        const projPerp = pd.panelDragData
          ? Math.abs(dx * pd.panelDragData.panelLocalY.x + dy * pd.panelDragData.panelLocalY.y)
          : 0;
        const goPanel = pd.panelDragData && projPerp > projWire;
        if (goPanel) {
          bundlePanelDragRef.current = {
            bundle: pd.bundle,
            panel: pd.panelDragData.panel,
            panelLocalY: pd.panelDragData.panelLocalY,
            startWorld: { x: pd.startWorld.x, y: pd.startWorld.y },
            cumulativeShift: 0,
            step: 5,
          };
        } else {
          slideDragRef.current = {
            bundle: pd.bundle,
            sliceKey: pd.sliceKey,
            sliceLen: pd.sliceLen,
            startMouse: { x: pd.startWorld.x, y: pd.startWorld.y },
            origSliceMid: pd.sliceMid,
            raf: 0,
            pending: null,
          };
        }
        pendingCollectorDragRef.current = null;
        // Mod commit edildi; aynı mousemove eventi'nde yeni mod'un işlemini de çalıştır
      }

      // Slice-slide drag: target = origSliceMid + mouse delta. target'ı sabit
      // guidePath'e projekte et → slice o noktaya, lokal teğete dik olarak
      // konumlanır. Mouse hat dışına çıksa da slice güzergâhta kalır; mouse
      // geri gelince slice de geri döner; köşelerde slice yönü güzergâhla döner.
      // SNAP: köşeye yakınsa o köşeye yapışır → wire'lar transition zone'a
      // girmez, rep line ve collector görsel olarak temiz kalır.
      if (slideDragRef.current) {
        const w = getWorld(e);
        const sd = slideDragRef.current;
        const target = {
          x: sd.origSliceMid.x + (w.x - sd.startMouse.x),
          y: sd.origSliceMid.y + (w.y - sd.startMouse.y),
        };
        let proj = projectOnGuidePath(sd.bundle, target);
        if (!proj) return;
        const beforeSnap = { x: proj.point.x, y: proj.point.y };
        proj = snapProjToCorner(sd.bundle, proj, sd.sliceKey);
        // Snap olduysa stubLen'i küçült — slice corner'a yakın olduğu için
        // büyük stubLen forwStubEnd'i corner'ın diğer tarafına geçirip wire'ı
        // bundle'dan düşürür. Snap yokken normal stubLen.
        const snapped = (Math.hypot(proj.point.x - beforeSnap.x, proj.point.y - beforeSnap.y) > 1e-3);
        const tangent = proj.tangent;
        const perp = { x: -tangent.y, y: tangent.x };
        const half = sd.sliceLen / 2;
        const newSlice = {
          a: { x: proj.point.x - perp.x * half, y: proj.point.y - perp.y * half },
          b: { x: proj.point.x + perp.x * half, y: proj.point.y + perp.y * half },
        };
        sd.pending = newSlice;
        sd.pendingSnapped = snapped;
        if (!sd.raf) {
          sd.raf = requestAnimationFrame(() => {
            const cur = slideDragRef.current;
            if (!cur) return;
            cur.raf = 0;
            const p = cur.pending;
            const wasSnap = cur.pendingSnapped;
            cur.pending = null;
            if (!p) return;
            try {
              cur.bundle.userData.slices[cur.sliceKey] = p;
              const back = wasSnap ? 5 : cur.bundle.userData.backStubLen;
              const forw = wasSnap ? 5 : cur.bundle.userData.forwStubLen;
              rebuildBundleWithStubs(scene, cur.bundle, back, forw);
            } catch (err) {
              console.error('[Bundle slide] rebuild error', err);
            }
          });
        }
        return;
      }

      // M-drag
      if (mDragRef.current) {
        const { bundle, side, startWorld, initialLen, followDir } = mDragRef.current;
        const w = getWorld(e);
        const dx = w.x - startWorld.x;
        const dy = w.y - startWorld.y;
        const signedDelta = dx * followDir.x + dy * followDir.y;
        let newLen = Math.max(5, initialLen + signedDelta);
        newLen = Math.min(1000, newLen);
        const currentBack = bundle.userData.backStubLen || 20;
        const currentForw = bundle.userData.forwStubLen || 20;
        const backLen = side === 'entry' ? newLen : currentBack;
        const forwLen = side === 'exit' ? newLen : currentForw;
        // rAF ile throttle: mousemove 60+ Hz'de tetiklenebilir, rebuild pahalı —
        // son hedefi sakla ve frame başına bir kez rebuild yap.
        mDragPendingRef.current = { bundle, backLen, forwLen };
        if (!mDragRafRef.current) {
          mDragRafRef.current = requestAnimationFrame(() => {
            mDragRafRef.current = 0;
            const p = mDragPendingRef.current;
            mDragPendingRef.current = null;
            if (!p) return;
            try {
              rebuildBundleWithStubs(scene, p.bundle, p.backLen, p.forwLen);
            } catch (err) {
              console.error('[Bundle M-drag] rebuild error', err);
            }
          });
        }
        return;
      }

      // 4-tıklama akışı: tooltip + aktif aşamanın 1. tıklamasından önizleme.
      // n=1 → click[0] hâlâ aktif aşamanın başlangıcı (BİRLEŞTİRME 2. nokta bekleniyor)
      // n=3 → click[2] aktif aşamanın başlangıcı (AÇMA 2. nokta bekleniyor)
      if (bundleModeRef.current) {
        moveTooltip(e.clientX, e.clientY);
        const n = clicksRef.current.length;
        if (n === 1 || n === 3) {
          updatePreviewLine(clicksRef.current[n - 1], getWorld(e));
        }
      }
    };

    const onMouseUp = (e) => {
      if (e.button !== 0) return;

      if (pendingCollectorDragRef.current) {
        // Click oldu ama threshold geçmedi (drag commit edilmedi) — temizle
        pendingCollectorDragRef.current = null;
        dom.style.cursor = '';
        // Mode'a girilmediyse devam etme; başka mode varsa düşmesin
        if (!bundlePanelDragRef.current && !slideDragRef.current) return;
      }

      if (bundlePanelDragRef.current) {
        e.preventDefault();
        e.stopPropagation();
        bundlePanelDragRef.current = null;
        dom.style.cursor = '';
        return;
      }

      if (slideDragRef.current) {
        e.preventDefault();
        e.stopPropagation();
        const sd = slideDragRef.current;
        if (sd.raf) {
          cancelAnimationFrame(sd.raf);
          sd.raf = 0;
        }
        if (sd.pending) {
          try {
            sd.bundle.userData.slices[sd.sliceKey] = sd.pending;
            rebuildBundleWithStubs(scene, sd.bundle,
              sd.bundle.userData.backStubLen,
              sd.bundle.userData.forwStubLen);
          } catch (err) { console.error('[Bundle slide] final rebuild error', err); }
        }
        slideDragRef.current = null;
        dom.style.cursor = '';
        return;
      }

      if (mDragRef.current) {
        e.preventDefault();
        e.stopPropagation();
        // Bekleyen rebuild varsa hemen uygula ki drag bittiğinde son state
        // ekranda kalsın.
        if (mDragRafRef.current) {
          cancelAnimationFrame(mDragRafRef.current);
          mDragRafRef.current = 0;
        }
        if (mDragPendingRef.current) {
          const p = mDragPendingRef.current;
          mDragPendingRef.current = null;
          try { rebuildBundleWithStubs(scene, p.bundle, p.backLen, p.forwLen); } catch (err) { console.error('[Bundle M-drag] final rebuild error', err); }
        }
        mDragRef.current = null;
        if (!mHeldRef.current) dom.style.cursor = '';
      }
      // İki-tıklama akışında mouseup ile bir şey yapmıyoruz; iş onMouseDown'da bitiyor.
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    dom.addEventListener('mousedown', onMouseDown, true);
    dom.addEventListener('mousemove', onMouseMove);
    dom.addEventListener('mouseup', onMouseUp, true);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      dom.removeEventListener('mousedown', onMouseDown, true);
      dom.removeEventListener('mousemove', onMouseMove);
      dom.removeEventListener('mouseup', onMouseUp, true);
      if (mDragRafRef.current) {
        cancelAnimationFrame(mDragRafRef.current);
        mDragRafRef.current = 0;
      }
      mDragPendingRef.current = null;
      if (slideDragRef.current?.raf) cancelAnimationFrame(slideDragRef.current.raf);
      slideDragRef.current = null;
      updatePreviewLine(null, null);
      setMergeSliceMesh(null, null);
      removeTooltip();
      clicksRef.current = [];
      dom.style.cursor = '';
    };
  }, [scene, camera, renderer, dispatch]);
}
